import React, { useState, useEffect } from 'react';
import AdminSubPage from '../components/AdminSubPage';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { CalendarClock, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

const FREQ_LABEL = { monthly: 'Monthly', quarterly: 'Quarterly', annual: 'Annual', event: 'Per-event' };

const ReportingCalendarPage = () => {
  const [items, setItems] = useState([]);
  const [config, setConfig] = useState({ fiscal_year_end_month: 3, reminder_offsets_days: [7, 3, 1], start_period_anchor: '' });
  const [loading, setLoading] = useState(true);

  useEffect(() => { load(); }, []);
  const load = async () => {
    setLoading(true);
    try {
      const [c, cfg] = await Promise.all([api.get('/reporting-calendar'), api.get('/reporting-calendar/config')]);
      setItems(c.data); setConfig(cfg.data);
    } catch { toast.error('Failed to load calendar'); }
    setLoading(false);
  };

  const saveItem = async (item, patch) => {
    try { await api.put(`/reporting-calendar/${item.id}`, patch); load(); }
    catch { toast.error('Failed'); }
  };

  const saveConfig = async () => {
    try {
      await api.put('/reporting-calendar/config', config);
      toast.success('Calendar config saved');
      // recompute due dates with new anchor / fiscal year
      await api.post('/reporting-calendar/recompute');
      load();
    } catch { toast.error('Failed'); }
  };

  const recompute = async () => {
    try { await api.post('/reporting-calendar/recompute'); toast.success('Due dates recomputed'); load(); }
    catch { toast.error('Failed'); }
  };

  const dispatchNow = async () => {
    try {
      const res = await api.post('/jobs/regulatory_reminder_dispatch/run-now');
      toast.success(`Dispatched: sent ${res.data.report?.sent ?? 0}, skipped ${res.data.report?.skipped_duplicate ?? 0}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Dispatch failed'); }
  };

  const dueClass = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr); const now = new Date();
    const days = Math.ceil((d - now) / (1000*60*60*24));
    if (days < 0) return 'text-red-600 font-bold';
    if (days <= 3) return 'text-amber-700 font-semibold';
    if (days <= 7) return 'text-amber-600';
    return 'text-gray-600';
  };

  return (
    <AdminSubPage title="Reporting Calendar" breadcrumb={['Admin', 'System', 'Reporting Calendar']} backPath="/dashboard/admin/system">
      <div className="bg-blue-50 border border-blue-200 px-4 py-3 mb-4 text-xs text-blue-800">
        <strong>How it works:</strong> Each entry's <em>Next due</em> is auto-computed from the statutory cadence + your fiscal year-end. The system fires in-app reminders to subscribed users at the configured offsets (default T-7 / T-3 / T-1). If your institution has a custom start period, set the <em>Start period anchor</em> below — cadences will count from that date.
      </div>

      {/* Global config */}
      <div className="bg-white border border-gray-200 p-5 mb-4">
        <h3 className="font-semibold text-[#1f1f1f] mb-3 flex items-center gap-2"><CalendarClock className="w-4 h-4 text-[#C5A059]" />Global calendar settings</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <Label className="text-xs">Fiscal Year-End Month</Label>
            <Select value={String(config.fiscal_year_end_month)} onValueChange={(v) => setConfig({ ...config, fiscal_year_end_month: parseInt(v) })}>
              <SelectTrigger data-testid="fy-end-month"><SelectValue /></SelectTrigger>
              <SelectContent>{[1,2,3,4,5,6,7,8,9,10,11,12].map(m => <SelectItem key={m} value={String(m)}>{['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'][m-1]}</SelectItem>)}</SelectContent>
            </Select>
            <p className="text-[10px] text-gray-400 mt-1">SA default: March (FY end 31 Mar).</p>
          </div>
          <div>
            <Label className="text-xs">Reminder offsets (days before due)</Label>
            <Input value={(config.reminder_offsets_days || []).join(',')} onChange={(e) => setConfig({ ...config, reminder_offsets_days: e.target.value.split(',').map(s => parseInt(s.trim())).filter(n => !isNaN(n)) })} placeholder="7,3,1" data-testid="reminder-offsets" />
            <p className="text-[10px] text-gray-400 mt-1">Comma-separated. Default 7,3,1.</p>
          </div>
          <div>
            <Label className="text-xs">Start period anchor (optional)</Label>
            <Input type="date" value={config.start_period_anchor || ''} onChange={(e) => setConfig({ ...config, start_period_anchor: e.target.value })} data-testid="start-anchor" />
            <p className="text-[10px] text-gray-400 mt-1">Leave blank for statutory defaults.</p>
          </div>
        </div>
        <div className="flex gap-2 mt-4">
          <Button onClick={saveConfig} className="bg-[#1f1f1f] text-white" data-testid="save-cal-config">Save</Button>
          <Button variant="outline" onClick={recompute} data-testid="recompute-btn"><RefreshCw className="w-4 h-4 mr-1" />Recompute due dates</Button>
          <Button variant="outline" onClick={dispatchNow} data-testid="dispatch-btn">Dispatch reminders now</Button>
        </div>
      </div>

      {/* Calendar items */}
      <div className="bg-white border border-gray-200" data-testid="calendar-table">
        <table className="w-full">
          <thead><tr className="bg-gray-50 border-b">
            <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Report</th>
            <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Frequency</th>
            <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Statutory basis</th>
            <th className="text-center px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Due offset (days)</th>
            <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Next due</th>
            <th className="text-center px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Active</th>
          </tr></thead>
          <tbody>
            {loading ? <tr><td colSpan={6} className="text-center py-8 text-gray-400">Loading...</td></tr> :
            items.map(item => (
              <tr key={item.id} className="border-b hover:bg-gray-50" data-testid={`cal-row-${item.report_key}`}>
                <td className="px-4 py-3">
                  <div className="font-medium text-sm text-[#1f1f1f]">{item.name}</div>
                  <div className="text-[10px] text-gray-400 uppercase tracking-wider">{item.report_key}</div>
                </td>
                <td className="px-4 py-3">
                  <Select value={item.frequency} onValueChange={(v) => saveItem(item, { frequency: v })}>
                    <SelectTrigger className="h-8 text-xs w-32"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {Object.entries(FREQ_LABEL).map(([k, label]) => <SelectItem key={k} value={k}>{label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </td>
                <td className="px-4 py-3 text-xs text-gray-500">{item.basis}</td>
                <td className="px-4 py-3 text-center">
                  <Input type="number" defaultValue={item.due_offset_days} className="w-20 text-xs h-8" onBlur={(e) => { const v = parseInt(e.target.value); if (v !== item.due_offset_days) saveItem(item, { due_offset_days: v }); }} data-testid={`offset-${item.report_key}`} />
                </td>
                <td className={`px-4 py-3 text-sm ${dueClass(item.next_due_date)}`}>{item.next_due_date || '—'}</td>
                <td className="px-4 py-3 text-center"><Switch checked={!!item.is_active} onCheckedChange={(v) => saveItem(item, { is_active: v })} data-testid={`active-${item.report_key}`} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </AdminSubPage>
  );
};
export default ReportingCalendarPage;
